import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, transitionBase, uiMarker, uiMarkerLayout } from './shared';

/**
 * THE PHONE'S CHROME — what frames every screen of the mobile app: the
 * floating tab bar along the bottom, the pills along the top, the header of
 * a screen opened from a list.
 *
 * Drawn to the Figma page structures. These float over whatever the screen
 * shows, often a map, so each carries its own surface and shadow rather than
 * sitting on a page background.
 */

/**
 * The tab bar's position: fixed along the bottom, clear of the home
 * indicator. It lets taps through around the pill, so the list or map
 * underneath stays usable either side of it.
 */
export const mobileTabBarRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4',
  'pb-[max(env(safe-area-inset-bottom),theme(spacing.4))]',
]);

/**
 * The dark pill itself, in the float material: 80% foreground over a glass
 * blur, so a map behind it shows through at the edges. Inverted from the
 * page, so it reads over a map.
 */
export const mobileTabBarListRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'pointer-events-auto flex items-center gap-6 rounded-pill bg-foreground/80 px-6 py-4',
  'text-background shadow-float backdrop-blur-glass',
]);

/**
 * One tab: an icon alone, its word for a screen reader. The Figma draws no
 * labels, so the current tab is told apart by brightness, full against half.
 */
export const mobileTabRecipe = cva(
  [
    uiMarker,
    'relative flex items-center justify-center rounded-pill',
    '[&_svg]:size-icon-lg [&_svg]:shrink-0',
    transitionBase,
    focusRing,
  ],
  {
    variants: {
      active: {
        true: 'text-background',
        false: 'text-background/50 hover:text-background',
      },
    },
    defaultVariants: { active: false },
  }
);

/** The count on a tab's icon: unread calls, an outbox waiting to send. */
export const mobileTabBadgeRecipe = cva([
  uiMarker,
  'absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-pill px-1',
  'bg-destructive font-sans text-overline text-destructive-foreground tabular-nums',
]);

/**
 * The presence pill at the top of a tab: who you are, then your duty status
 * in a white chip of its own. The Figma's App Header draws it as a faint
 * wash rather than a card, centred, so it reads as part of the screen's
 * chrome and not as content.
 */
export const presencePillRecipe = cva([
  uiMarker,
  'inline-flex h-control-sm max-w-full items-center gap-2 rounded-pill bg-foreground/5 py-0.5 pl-1.5 pr-0.5',
  'font-sans text-foreground hover:bg-foreground/10',
  transitionBase,
  focusRing,
]);

/** The duty status, in the white chip at the pill's right-hand end. */
export const presencePillStatusRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'inline-flex h-7 shrink-0 items-center gap-1 rounded-pill bg-background pl-2 pr-2.5',
]);

/**
 * Where the active-call pill rests: centred just above the tab bar, which is
 * 56px of pill (spacing.14) clear of the home indicator, with 16px between
 * the two. Taps pass either side of it, as they do around the tab bar.
 */
export const activeCallDockRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4',
  'bottom-[calc(max(env(safe-area-inset-bottom),theme(spacing.4))+theme(spacing.14)+theme(spacing.4))]',
]);

/**
 * The active-call pill, the Figma's Status Context: a white pill held in a
 * faint wash, in the float material, so it lifts off whatever scrolls under
 * it the way the tab bar does.
 */
export const activeCallPillRecipe = cva([
  uiMarker,
  'pointer-events-auto inline-flex h-control-xl max-w-full items-center rounded-pill bg-foreground/5 p-0.5',
  'font-sans shadow-float backdrop-blur-glass',
  transitionBase,
  focusRing,
]);

export const activeCallPillInnerRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex h-full min-w-0 items-center gap-4 rounded-pill bg-card pl-4 text-card-foreground',
  ],
  {
    variants: {
      /** A status chip at the end sits tight in the curve; words need room. */
      end: { chip: 'pr-2', text: 'pr-4' },
    },
    defaultVariants: { end: 'chip' },
  }
);

/**
 * A whole phone screen that scrolls: the page's ground edge to edge, the
 * pills or a header along the top, then the content in the screen's gutter.
 * The shell's screens are library-only, and a gutter, the notch and room for
 * the tab bar are padding, which a call site may not add, so the screen
 * itself is the library's.
 */
export const mobileScreenRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex min-h-dvh w-full min-w-0 flex-col bg-background text-foreground',
  ],
  {
    variants: {
      /**
       * A column of reading width on a wide screen, centred: a chat, whose
       * lines and cards stretched across a desktop are hard to follow. On a
       * phone it is the phone's width, as before.
       */
      narrow: {
        true: 'mx-auto max-w-2xl',
        false: '',
      },
    },
    defaultVariants: { narrow: false },
  }
);

/**
 * The presence pill along the top: centred, as the Figma's App Header puts
 * it, and clear of the notch.
 */
export const mobileScreenOverlayRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-wrap items-center justify-center gap-2 px-4 pt-[max(env(safe-area-inset-top),theme(spacing.3))]',
]);

/** A MobileHeader along the top, held there while the content scrolls. */
export const mobileScreenHeaderRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'sticky top-0 z-30',
]);

export const mobileScreenBodyRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex min-w-0 flex-1 flex-col gap-4 px-4 pt-4'],
  {
    variants: {
      /**
       * The floating tab bar is over this screen: leave it, the active-call
       * pill docked above it and the home indicator clear at the bottom, so
       * the last row can scroll out from under both. Room for the pill is
       * left whether or not there is a call; a screen that jumped when one
       * started would be worse than a little extra scroll.
       */
      tabBar: {
        true: 'pb-[calc(max(env(safe-area-inset-bottom),theme(spacing.4))+theme(spacing.36))]',
        false: 'pb-[max(env(safe-area-inset-bottom),theme(spacing.4))]',
      },
      /** Nothing above the content, so the content clears the notch itself. */
      bare: {
        true: 'pt-[max(env(safe-area-inset-top),theme(spacing.4))]',
        false: '',
      },
    },
    defaultVariants: { tabBar: false, bare: false },
  }
);

/**
 * What stays along the bottom of a page while it scrolls, the Figma's
 * Footer: a call's status, centred and clear of the home indicator, on a
 * fade from nothing to the page so the content passing under it is not cut
 * off by a hard edge. The call screen with no map is the case; over a map
 * the call's sheet has its own footer.
 */
export const mobileScreenFooterRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'sticky bottom-0 z-30 flex items-center gap-4 pt-4',
    'bg-gradient-to-b from-background/0 to-background',
  ],
  {
    variants: {
      /**
       * `fill` spans the screen's gutter rather than centring in from it:
       * a chat's composer, which is as wide as the content it writes into.
       */
      fill: {
        true: 'justify-stretch px-4 pb-[max(env(safe-area-inset-bottom),theme(spacing.3))]',
        false:
          'justify-center px-12 pb-[max(env(safe-area-inset-bottom),theme(spacing.8))]',
      },
    },
    defaultVariants: { fill: false },
  }
);

/**
 * The header of a screen opened from somewhere: back on the left, what the
 * screen is about in the middle, one action on the right. `floating` is the
 * call detail's, over a map, with no bar of its own; `solid` is a page's.
 */
export const mobileHeaderRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'grid grid-cols-[1fr_auto_1fr] items-center gap-2',
  ],
  {
    variants: {
      surface: {
        floating: '',
        // No rule under it: the Figma's nested header sits on the page, and
        // the first divider is under the title block that follows.
        solid:
          'bg-background px-4 pb-2 pt-[max(env(safe-area-inset-top),theme(spacing.2))]',
      },
    },
    defaultVariants: { surface: 'solid' },
  }
);

export type MobileHeaderRecipeProps = VariantProps<typeof mobileHeaderRecipe>;

/**
 * SCAN FRAME — the camera screen that reads something off a vehicle: a VIN
 * off a windscreen or a door pillar, an impound's QR tag at the lot. Dark
 * whatever the theme, because it frames a camera feed. The feed fills the
 * screen behind a guide box the shape of what is being read; everything
 * outside the box is dimmed so the eye goes to it.
 */
export const scanFrameRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'fixed inset-0 z-50 flex flex-col bg-foreground text-background',
  'pb-[max(env(safe-area-inset-bottom),theme(spacing.4))] pt-[max(env(safe-area-inset-top),theme(spacing.4))]',
]);

/** The camera feed the app passes in, stretched behind everything. */
export const scanFrameFeedRecipe = cva([
  uiMarker,
  'absolute inset-0 [&>*]:size-full [&>*]:object-cover',
]);

/**
 * The guide box. A VIN plate is long and low; a QR tag is square, and
 * smaller, because a phone held at arm's length reads one from further off.
 */
export const scanFrameGuideRecipe = cva(
  [
    uiMarker,
    'relative rounded-lg border-2 border-background',
    'shadow-[0_0_0_100vmax_hsl(var(--foreground)/0.6)]',
  ],
  {
    variants: {
      shape: {
        plate: 'aspect-[5/1] w-full',
        square: 'aspect-square w-3/4 max-w-xs',
      },
    },
    defaultVariants: { shape: 'plate' },
  }
);

export type ScanFrameGuideRecipeProps = VariantProps<
  typeof scanFrameGuideRecipe
>;

/**
 * Where the guide box was, when the camera cannot open: a light panel, so
 * what the app says about it reads as it would anywhere else.
 */
export const scanFrameNoticeRecipe = cva([
  uiMarker,
  'relative w-full max-w-sm rounded-lg bg-background text-foreground shadow-e3',
]);

/**
 * The live camera itself: it fills what holds it and crops rather than
 * letterboxes, so the guide box sits over picture edge to edge. Dark
 * behind, for the moment before the first frame arrives.
 */
export const cameraFeedRecipe = cva([uiMarker, 'bg-foreground object-cover'], {
  variants: {
    /**
     * `true` in a sheet rather than behind a ScanFrame, as the Figma's
     * Enter VIN sheet draws it (460:83400): the section's width at a
     * card's corners, taller than wide, the picture cropped to fill it.
     */
    framed: {
      true: 'aspect-[2/3] w-full rounded-2xl',
      false: 'size-full',
    },
  },
  defaultVariants: { framed: false },
});

/** The round shutter button along the bottom. */
export const scanFrameShutterRecipe = cva([
  uiMarker,
  'size-[4.5rem] rounded-pill border-4 border-background bg-background/20',
  'active:bg-background/40',
  transitionBase,
  focusRing,
]);

/**
 * STATUS CHIP — the timer half of a call's status chip: "• 4:21". The
 * separator is quiet and the time is in the page's own colour, so the
 * status word keeps its tone and the clock reads as a fact beside it.
 */
export const statusChipSeparatorRecipe = cva([uiMarker, 'opacity-50']);

export const statusChipTimeRecipe = cva([
  uiMarker,
  'text-body-lg-strong text-foreground tabular-nums',
]);

/**
 * STATUS CONTROL — a call's status as the control that changes it, the
 * Figma's Status Control: a white pill held in a faint wash, the status's
 * dot and word on the left and a caret on the right. Full width at the foot
 * of a call, over a map or not, as both of the Figma's call frames draw it;
 * hugging its content where it stands alone.
 */
export const statusControlRecipe = cva(
  [
    uiMarker,
    'inline-flex h-control-xl items-center rounded-pill bg-foreground/5 p-0.5',
    'font-sans text-foreground disabled:pointer-events-none disabled:opacity-50',
    transitionBase,
    focusRing,
  ],
  {
    variants: {
      fullWidth: { true: 'w-full', false: '' },
    },
    defaultVariants: { fullWidth: false },
  }
);

export const statusControlInnerRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex h-full flex-1 items-center justify-between gap-3 rounded-pill bg-background px-3',
]);

/**
 * DETAIL FIELD — a label over its value, with a round button at the right
 * when there is something to do with it: the call's Customer and Pickup.
 */
export const detailFieldRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex min-w-0 items-center justify-between gap-4',
]);

/**
 * ADD TILE — the way in to something a call does not have yet: "Add
 * photos", "Add VIN", "Add note". A dashed outline, a small plus in a wash
 * and the words muted, as the Figma draws the empty sections of a call, so
 * an empty section reads as a place to put something rather than a gap.
 */
export const addTileRecipe = cva(
  [
    uiMarker,
    'inline-flex items-center justify-center border border-foreground/10',
    'font-sans text-muted-foreground hover:bg-foreground/5',
    'disabled:pointer-events-none disabled:opacity-50',
    transitionBase,
    focusRing,
  ],
  {
    variants: {
      /** `block` spans the section, for photos; `inline` hugs its words. */
      width: {
        block: 'w-full py-8',
        inline: 'p-3',
      },
      /**
       * `dashed` is the way in to something a call does not have yet: Add
       * photos, Add VIN, Add note. `solid` is a choice to make before the
       * form is done, the create sheet's "Add an operator": a plain box,
       * 88 high, a large glyph and the words at heading size.
       */
      appearance: {
        dashed: 'gap-2 rounded-2xl border-dashed text-body-lg',
        solid: 'h-[5.5rem] gap-4 rounded-md bg-background text-h2',
      },
      /**
       * 64 high rather than 88, for a block inside a form or under a camera:
       * the vehicle sheet's Scan VIN and the Enter VIN sheet's Add VIN
       * Manually (865:32350, 460:83400).
       */
      compact: { true: '', false: '' },
    },
    compoundVariants: [
      // The solid box sets its own height; the block's padding would add to it.
      { appearance: 'solid', width: 'block', class: 'py-0' },
      { appearance: 'dashed', width: 'block', compact: true, class: 'py-5' },
    ],
    defaultVariants: { width: 'inline', appearance: 'dashed', compact: false },
  }
);

/** The small plus in a wash at the front of an add tile. */
export const addTileGlyphRecipe = cva([
  uiMarker,
  'flex size-6 shrink-0 items-center justify-center rounded-pill bg-foreground/5 text-foreground',
  '[&_svg]:size-icon-sm',
]);

/**
 * The VIN the camera read, in the box the Figma's Processing VIN sheet draws
 * it in: an input's outline, centred, so it reads as something that can be
 * changed. Tapping it is "Tap to edit".
 */
export const vinConfirmValueRecipe = cva([
  uiMarker,
  'flex h-control-xl w-full items-center justify-center rounded-2xl border border-foreground/10 bg-background px-3',
  'font-mono text-body-lg-strong text-foreground hover:bg-foreground/5',
  transitionBase,
  focusRing,
]);

/**
 * A note on a call, NoteCard's outline appearance, as the Figma's Add note
 * frames draw one, and the call's vehicle in VehicleCard (865:32414): a
 * hairline outline at the section's width, so what it holds reads as the
 * section's own content rather than a card laid on it. The Add note and
 * Add vehicle tiles that stand in for them are the dashed ones.
 */
export const outlineCardRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'block w-full rounded-2xl border border-foreground/10 p-3 text-left',
  ],
  {
    variants: {
      /** Opens to change it: the viewer's own note, a vehicle they may change. */
      interactive: {
        true: ['hover:bg-foreground/5', transitionBase, focusRing],
        false: '',
      },
    },
    defaultVariants: { interactive: false },
  }
);

/**
 * A comment under a photo, as the Figma's Comment Appear frames draw one:
 * the writer's face at the left, top-aligned so a long comment wraps under
 * the name and not under the face.
 */
export const commentRowRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-start gap-2', transitionBase],
  {
    variants: {
      /** Sent and not yet saved: faded until the server has it. */
      pending: { true: 'opacity-50', false: '' },
    },
    defaultVariants: { pending: false },
  }
);

/**
 * The comment box at the foot of a photo: one pill holding a chat glyph,
 * the words and a round send button, as the Figma's Quick Comment frames
 * draw it. The pill shows the focus, since the input inside has no ring.
 */
export const commentComposerRecipe = cva([
  uiMarker,
  'flex min-h-control-xl w-full items-center gap-2 rounded-pill border border-foreground/10 bg-background py-1 pl-4 pr-1',
  'text-muted-foreground focus-within:ring-focus focus-within:ring-ring',
  transitionBase,
]);

/** The small map of the pickup in the assigned-call prompt. */
export const assignedCallMapRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'h-28 overflow-hidden rounded-lg',
]);

/**
 * MOBILE INTRO — a phone page that asks one thing of a person before the
 * app proper: the Figma's Welcome to Relay (123:7108) and its setup
 * checklist (123:7153). The brand and a centred title over a muted line,
 * what the page is about under them, and its answers held along the
 * bottom: a big pill and a quieter way out under it. The page's own gutter
 * and the notch and home indicator are kept clear here, since a call site
 * may not pad.
 */
export const mobileIntroRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex min-h-dvh w-full min-w-0 flex-col bg-background px-6 text-foreground',
  'pt-[max(env(safe-area-inset-top),theme(spacing.6))]',
  'pb-[max(env(safe-area-inset-bottom),theme(spacing.6))]',
]);

export const mobileIntroBodyRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex min-w-0 flex-1 flex-col gap-8'],
  {
    variants: {
      /**
       * `center` is the Welcome frame's: the title in the middle of the
       * screen. `top` is the setup checklist's: the title at the top with
       * a list under it.
       */
      align: {
        center: 'justify-center',
        top: 'pt-6',
      },
    },
    defaultVariants: { align: 'center' },
  }
);

/** The brand, the title and the muted line under it, centred. */
export const mobileIntroHeadRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col items-center gap-4 text-center',
]);

/** The answers along the bottom: the pill, and the link under it. */
export const mobileIntroActionsRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col items-stretch gap-4 pt-6',
]);

/**
 * ICON FAN — what an app is about to ask for, as the Figma's Welcome to
 * Relay draws it (123:7108): a row of white cards, each with one coloured
 * glyph, overlapping a little and tipped alternately left and right as if
 * dealt onto the page.
 */
export const iconFanRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex items-center justify-center py-2',
]);

export const iconFanCardRecipe = cva([
  uiMarker,
  'flex size-16 shrink-0 items-center justify-center rounded-2xl bg-card shadow-e2',
  '-mx-1 odd:-rotate-6 even:rotate-6',
]);

/**
 * SETUP CHECKLIST — what is left before a new company can operate, as the
 * Figma's setup checklist draws it (123:7153): a list of steps with a rule
 * between each, every step a ring that fills with a tick once it is done,
 * the step's name over a muted line, and a caret at the right.
 */
export const setupChecklistRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col divide-y divide-foreground/10',
]);

export const setupStepRecipe = cva([
  uiMarker,
  'flex w-full items-center gap-4 py-6 text-left first:pt-0 last:pb-0',
  'disabled:pointer-events-none disabled:opacity-50',
  transitionBase,
  focusRing,
]);

export const setupStepMarkRecipe = cva(
  [
    uiMarker,
    'flex size-8 shrink-0 items-center justify-center rounded-pill [&_svg]:size-icon-sm',
  ],
  {
    variants: {
      done: {
        true: 'bg-success text-success-foreground',
        false: 'bg-foreground/5',
      },
    },
    defaultVariants: { done: false },
  }
);

/**
 * A tab's page on a card, as the Figma's Functions screens draw it: white,
 * a hairline and a soft shadow, standing 8px in from the screen's edges,
 * half the gutter, and reaching the bottom however short the list. The 16px
 * inside lines the title up with a list's toolbar, whose own inset the list
 * takes back with `bleed`.
 */
export const pageCardRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  '-mx-2 flex flex-1 flex-col gap-3 rounded-lg border border-border bg-card px-4 pb-4 pt-4',
  'text-card-foreground shadow-e1',
]);

/** The card's top row: the title, or its PageMenu, then the actions. */
export const pageCardHeaderRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex min-h-control-lg items-center justify-between gap-3',
]);

/** A PageMenu's trigger: the page's title, with a caret that says it opens. */
export const pageMenuTriggerRecipe = cva([
  uiMarker,
  'inline-flex max-w-full items-center gap-1 rounded-pill text-foreground',
  '[&_svg]:size-icon-sm [&_svg]:shrink-0',
  focusRing,
]);

/**
 * The menu it opens, in the tab bar's material: 80% foreground over a
 * glass blur with the page's colour on it, the width of the screen less
 * 12px a side.
 */
export const pageMenuContentRecipe = cva([
  uiMarker,
  'z-50 w-[calc(100vw-theme(spacing.6))] max-w-sm rounded-lg bg-foreground/80 p-4',
  'text-background shadow-e3 backdrop-blur-glass',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
]);

/**
 * The pages, 16px apart with a rule between each. On the group rather than
 * the menu: the rows are the group's children, and a gap on the menu left
 * them touching.
 */
export const pageMenuGroupRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col gap-4',
]);

/** One page: its icon and name, a dot at the end for the one open. */
export const pageMenuItemRecipe = cva([
  uiMarker,
  'flex cursor-default select-none items-center gap-2 rounded-sm font-sans text-h2 outline-none',
  'focus-visible:bg-background/10 [&_svg]:size-icon-md [&_svg]:shrink-0',
]);

/** The rule between two pages. */
export const pageMenuSeparatorRecipe = cva([uiMarker, 'h-px bg-background/10']);

/** The dot that marks the page open. */
export const pageMenuDotRecipe = cva([
  uiMarker,
  'ml-auto size-3 shrink-0 rounded-pill bg-background',
]);

/**
 * The pull-to-refresh indicator along the top of a MobileScreen: as tall as
 * the pull, so the screen moves down with the finger, and eased back up
 * once it lets go.
 */
export const mobilePullRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex shrink-0 items-end justify-center overflow-hidden text-muted-foreground',
  ],
  {
    variants: {
      dragging: {
        true: '',
        false: 'transition-[height] duration-200 ease-out',
      },
    },
    defaultVariants: { dragging: false },
  }
);

/** The arrow in it, which turns over once the pull is far enough. */
export const mobilePullArrowRecipe = cva(
  [uiMarker, 'mb-3 size-icon-lg transition-transform duration-150'],
  {
    variants: { ready: { true: 'rotate-180', false: '' } },
    defaultVariants: { ready: false },
  }
);
